import React, { useState, useEffect } from "react";
import "./AutoLogModal.css";

interface VehicleLog {
    id: string;
    model: string;
    totalMileage: number; // Cumulative odometer
    co2: number;
    depot: string;
}

interface AutoLogModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedVehicles: { id: string; model: string; driver: string; mileage: number; co2: number; depot: string }[];
}

const ALL_DEPOTS = ["전체", "강남", "서초", "마포", "동부산", "인천"];

// Mock data generator for adding by depot
const getDepotVehicles = (depot: string): VehicleLog[] => {
    const mockTemplates = [
        { model: '봉고 III EV', avgKmPerDay: 45 },
        { model: '아이오닉 5', avgKmPerDay: 60 },
        { model: 'EV6', avgKmPerDay: 35 },
        { model: '포터 II Electric', avgKmPerDay: 55 },
    ];
    
    const targets = depot === "전체" ? ALL_DEPOTS.slice(1) : [depot];
    const results: VehicleLog[] = [];
    
    // Assume roughly a 20-30 day period for the total
    const periodDays = 20 + Math.floor(Math.random() * 10);
    
    targets.forEach(d => {
        [1, 2].forEach(i => {
            const template = mockTemplates[Math.floor(Math.random() * Number(mockTemplates.length))];
            const periodKm = template.avgKmPerDay * periodDays;
            results.push({
                id: `${10 + Math.floor(Math.random() * 80)}가 ${1000 + Math.floor(Math.random() * 8000)}`,
                model: template.model,
                totalMileage: periodKm + Math.floor(Math.random() * 100), // Total within period
                co2: (periodKm * 0.15) + Math.floor(Math.random() * 20),
                depot: d
            });
        });
    });
    return results;
};

export default function AutoLogModal({ isOpen, onClose, selectedVehicles: initialVehicles }: AutoLogModalProps) {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [email, setEmail] = useState("");
    const [memo, setMemo] = useState("");
    const [vehicleList, setVehicleList] = useState<VehicleLog[]>([]);
    const [searchId, setSearchId] = useState("");
    const [selectedDepot, setSelectedDepot] = useState("전체");

    const [sendMode, setSendMode] = useState<'instant' | 'auto'>('instant');
    const [autoDay, setAutoDay] = useState("1");
    const [autoTime, setAutoTime] = useState("09:00");
    const [autoTarget, setAutoTarget] = useState("전체 법인 차량");
    const [hasExistingConfig, setHasExistingConfig] = useState(false);

    const isPeriodSelected = startDate !== "" && endDate !== "";

    useEffect(() => {
        if (isOpen) {
            // Convert initial vehicles to the new format with period totals
            const converted = initialVehicles.map(v => ({
                id: v.id,
                model: v.model,
                totalMileage: v.mileage,
                co2: v.co2,
                depot: v.depot
            }));
            setVehicleList(converted);

            // Load existing auto-send configuration
            const savedConfig = localStorage.getItem('driving_auto_report_config');
            if (savedConfig) {
                try {
                    const config = JSON.parse(savedConfig);
                    setEmail(config.email || "");
                    setMemo(config.memo || "");
                    setAutoDay(config.autoDay || "1");
                    setAutoTime(config.autoTime || "09:00");
                    setAutoTarget(config.autoTarget || "전체 법인 차량");
                    setHasExistingConfig(true);
                } catch (e) {
                    console.error("Failed to parse auto-send config", e);
                }
            } else {
                setHasExistingConfig(false);
            }
        }
    }, [isOpen, initialVehicles]);

    if (!isOpen) return null;

    const handleAddBySearch = () => {
        if (!isPeriodSelected || !searchId.trim()) return;
        const newVehicle: VehicleLog = {
            id: searchId,
            model: "봉고 III EV",
            totalMileage: 150 + Math.floor(Math.random() * 300),
            co2: 25 + Math.floor(Math.random() * 40),
            depot: "기타"
        };
        if (!vehicleList.find(v => v.id === newVehicle.id)) {
            setVehicleList(prev => [...prev, newVehicle]);
        }
        setSearchId("");
    };

    const handleAddByDepot = () => {
        if (!isPeriodSelected) return;
        const newVehicles = getDepotVehicles(selectedDepot);
        setVehicleList(prev => {
            const existingIds = new Set(prev.map(v => v.id));
            const uniqueNew = newVehicles.filter(v => !existingIds.has(v.id));
            return [...prev, ...uniqueNew];
        });
    };

    const handleRemoveVehicle = (id: string) => {
        setVehicleList(prev => prev.filter(v => v.id !== id));
    };

    const handleSend = () => {
        if (sendMode === 'auto') {
            const timeMap: Record<string, string> = { "09:00": "오전 9시", "10:00": "오전 10시", "13:00": "오후 1시", "18:00": "오후 6시" };
            const dayStr = autoDay === "末" ? "말일" : `${autoDay}일`;
            
            // Save configuration to simulated DB (localStorage)
            const config = { email, memo, autoDay, autoTime, autoTarget };
            localStorage.setItem('driving_auto_report_config', JSON.stringify(config));

            alert(`${email} 주소로 매월 [${dayStr} ${timeMap[autoTime]}] 마다 [${autoTarget}] 대상 전월 기록을 자동 발송하도록 ${hasExistingConfig ? '수정' : '예약'}했습니다.\n\n(참고: 메모 내 {YYYY}와 {MM}은 발송 시점 날짜로 치환됩니다.)`);
        } else {
            alert(`${email} 주소로 (${startDate}~${endDate}) 기간의 운행기록부 전송을 완료했습니다.`);
        }
        onClose();
    };

    const handleDeleteConfig = () => {
        if (window.confirm("현재 설정된 정기 자동 발송 예약을 삭제하시겠습니까?")) {
            localStorage.removeItem('driving_auto_report_config');
            alert("자동 발송 예약이 취소되었습니다.");
            setHasExistingConfig(false);
            onClose();
        }
    };

    return (
        <div className="custom-modal-backdrop">
            <div className="tax-log-modal-container" style={{ maxWidth: '950px' }}>
                <div className="tax-log-header">
                    <div className="log-title-wrap">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                        </svg>
                        <h2>운행기록 이메일 전송</h2>
                    </div>
                    <button className="log-close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="tax-log-body">
                    <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '24px' }}>
                        <button 
                            onClick={() => setSendMode('instant')}
                            style={{ flex: 1, padding: '12px', background: 'none', border: 'none', borderBottom: sendMode === 'instant' ? '3px solid #2563eb' : '3px solid transparent', color: sendMode === 'instant' ? '#2563eb' : '#64748b', fontWeight: 700, cursor: 'pointer', fontSize: '14px', outline: 'none' }}
                        >
                            일회성 즉시 발송
                        </button>
                        <button 
                            onClick={() => setSendMode('auto')}
                            style={{ flex: 1, padding: '12px', background: 'none', border: 'none', borderBottom: sendMode === 'auto' ? '3px solid #2563eb' : '3px solid transparent', color: sendMode === 'auto' ? '#2563eb' : '#64748b', fontWeight: 700, cursor: 'pointer', fontSize: '14px', outline: 'none' }}
                        >
                            매월 정기 자동 발송 예약
                        </button>
                    </div>

                    {sendMode === 'instant' ? (
                        <>
                            {/* Toolbar: Period -> Depot -> Search */}
                            <div className="fleet-add-toolbar" style={{ 
                                display: 'flex', 
                                alignItems: 'flex-end', 
                                gap: '20px', 
                                marginBottom: '24px', 
                                padding: '20px', 
                                background: '#f8fafc', 
                                borderRadius: '12px', 
                                border: '1px solid #e2e8f0' 
                            }}>
                                {/* 1. Period Selection */}
                                <div style={{ flex: 'none' }}>
                                    <label style={{ fontSize: '13px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>
                                <span style={{ color: '#ef4444', marginRight: '4px' }}>*</span>운행 기간 선택
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input 
                                    type="date" 
                                    className="toolbar-date-input" 
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                                />
                                <span style={{ color: '#94a3b8' }}>~</span>
                                <input 
                                    type="date" 
                                    className="toolbar-date-input" 
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                                />
                            </div>
                        </div>

                        {/* 2. Depot Batch Add */}
                        <div style={{ flex: 2, opacity: isPeriodSelected ? 1 : 0.5 }}>
                            <label style={{ fontSize: '13px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>지점별 일괄 추가</label>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <select 
                                    className="depot-select" 
                                    disabled={!isPeriodSelected}
                                    value={selectedDepot}
                                    onChange={(e) => setSelectedDepot(e.target.value)}
                                    style={{ flex: 3, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: 'white', outline: 'none', height: '37px' }}
                                >
                                    {ALL_DEPOTS.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                                <button 
                                    className="btn-add-action" 
                                    disabled={!isPeriodSelected}
                                    onClick={handleAddByDepot}
                                    style={{ 
                                        flex: 1,
                                        minWidth: '80px',
                                        height: '37px', 
                                        background: isPeriodSelected ? '#f1f5f9' : '#f1f5f9', 
                                        color: '#475569', 
                                        border: '1px solid #cbd5e1', 
                                        borderRadius: '6px', 
                                        fontSize: '13px', 
                                        fontWeight: 600, 
                                        cursor: isPeriodSelected ? 'pointer' : 'not-allowed' 
                                    }}
                                >
                                    추가
                                </button>
                            </div>
                        </div>

                        {/* 3. Search Add */}
                        <div style={{ width: '180px', opacity: isPeriodSelected ? 1 : 0.5 }}>
                            <label style={{ fontSize: '13px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>차량 번호 검색</label>
                            <div style={{ display: 'flex' }}>
                                <input 
                                    type="text" 
                                    className="search-add-input" 
                                    disabled={!isPeriodSelected}
                                    value={searchId}
                                    onChange={(e) => setSearchId(e.target.value)}
                                    placeholder="예: 12가" 
                                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #cbd5e1', borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px', fontSize: '13px', outline: 'none', width: '80px' }}
                                />
                                <button 
                                    className="btn-search-add" 
                                    disabled={!isPeriodSelected}
                                    onClick={handleAddBySearch}
                                    style={{ 
                                        padding: '0 12px', 
                                        background: isPeriodSelected ? '#2563eb' : '#94a3b8', 
                                        color: 'white', 
                                        border: 'none', 
                                        borderTopRightRadius: '6px', 
                                        borderBottomRightRadius: '6px', 
                                        cursor: isPeriodSelected ? 'pointer' : 'not-allowed', 
                                        fontSize: '13px', 
                                        fontWeight: 600 
                                    }}
                                >
                                    추가
                                </button>
                            </div>
                        </div>
                    </div>

                    {!isPeriodSelected && (
                        <div style={{ textAlign: 'center', padding: '16px', color: '#6366f1', background: '#eef2ff', borderRadius: '8px', fontSize: '13px', fontWeight: 600, marginBottom: '20px' }}>
                            ℹ️ 차량을 추가하려면 먼저 운행 기간을 선택해주세요.
                        </div>
                    )}

                    <div className="tax-notice-banner" style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <p style={{ margin: 0 }}>전송 패키지에 <strong>{vehicleList.length}대</strong>가 포함되었습니다. 목록에서 필요한 차량을 편집하세요.</p>
                    </div>

                    <div className="tax-log-table-wrapper" style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                        <table className="tax-log-table">
                            <thead>
                                <tr>
                                    <th>차량번호</th>
                                    <th>지점</th>
                                    <th>차종</th>
                                    <th style={{ textAlign: 'right' }}>운행 합계(km)</th>
                                    <th style={{ textAlign: 'right' }}>탄소감축 합계(kg)</th>
                                    <th style={{ textAlign: 'center' }}>삭제</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicleList.map((v) => (
                                    <tr key={v.id}>
                                        <td style={{ fontWeight: 700, color: '#0f172a' }}>{v.id}</td>
                                        <td style={{ color: '#64748b' }}>{v.depot}</td>
                                        <td style={{ fontSize: '13px' }}>{v.model}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{v.totalMileage.toLocaleString()}</td>
                                        <td style={{ textAlign: 'right', color: '#10b981', fontWeight: 600 }}>{v.co2.toLocaleString()}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button 
                                                className="btn-row-delete" 
                                                onClick={() => handleRemoveVehicle(v.id)}
                                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px' }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {vehicleList.length === 0 && (
                                    <tr>
                                        <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>전송할 차량 정보가 없습니다. 지점별 추가 또는 검색을 이용하세요.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                        </>
                    ) : (
                        <div style={{ marginBottom: '24px' }}>
                            {hasExistingConfig && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '12px', marginBottom: '20px' }}>
                                    <div style={{ background: '#10b981', color: 'white', padding: '6px', borderRadius: '50%', display: 'flex' }}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#065f46' }}>기존 예약 정보가 존재합니다.</div>
                                        <div style={{ fontSize: '13px', color: '#047857' }}>현재 설정된 규칙에 따라 매월 자동 발송 중입니다. 아래 폼에서 내용을 수정하거나 취소하실 수 있습니다.</div>
                                    </div>
                                </div>
                            )}

                            <div style={{ padding: '24px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <h3 style={{ fontSize: '15px', color: '#0f172a', marginBottom: '8px' }}>자동 발송 규칙 설정</h3>
                                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '24px' }}>지정된 일정에 맞춰 해당 그룹 차량의 전월 운행 기록 데이터를 실시간으로 집계하여 이메일로 자동 전송합니다.</p>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                                    <div>
                                        <label style={{ fontSize: '13px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>발송 주기 (일자 및 시간 지정)</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <select 
                                                value={autoDay} 
                                                onChange={(e) => setAutoDay(e.target.value)}
                                                style={{ flex: 1, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', outline: 'none' }}>
                                                <option value="1">매월 1일</option>
                                                <option value="5">매월 5일</option>
                                                <option value="10">매월 10일</option>
                                                <option value="15">매월 15일</option>
                                                <option value="末">매월 말일</option>
                                            </select>
                                            <select 
                                                value={autoTime} 
                                                onChange={(e) => setAutoTime(e.target.value)}
                                                style={{ flex: 1, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', outline: 'none' }}>
                                                <option value="09:00">오전 09:00</option>
                                                <option value="10:00">오전 10:00</option>
                                                <option value="13:00">오후 01:00</option>
                                                <option value="18:00">오후 06:00</option>
                                            </select>
                                        </div>
                                        <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', lineHeight: 1.5 }}>
                                            * 발송일 기준 항상 <strong style={{color: '#0369a1'}}>직전 달(1일~말일)</strong>의 데이터가 집계됩니다.
                                        </p>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '13px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>발송 대상 차량 (동적 그룹)</label>
                                        <select 
                                            value={autoTarget} 
                                            onChange={(e) => setAutoTarget(e.target.value)} 
                                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                                        >
                                            <option value="전체 법인 차량">전체 법인 차량</option>
                                            <option value="동부산 지점 차량">동부산 지점 소속 차량 전체</option>
                                            <option value="인천 지점 차량">인천 지점 소속 차량 전체</option>
                                            <option value="마포 지점 차량">마포 지점 소속 차량 전체</option>
                                            <option value="강남 지점 차량">강남 지점 소속 차량 전체</option>
                                            <option value="서초 지점 차량">서초 지점 소속 차량 전체</option>
                                        </select>
                                        <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', lineHeight: 1.5 }}>
                                            * 고정된 목록이 아닌 발송 시점에 그룹에 속해있는 차량을 <strong style={{color: '#0369a1'}}>실시간 조회</strong>합니다.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="email-config-section" style={{ marginTop: '24px', padding: '24px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 700, color: '#0369a1', display: 'block', marginBottom: '8px' }}>수신 이메일</label>
                                <input 
                                    type="email" 
                                    placeholder="manager@company.com" 
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    style={{ width: '100%', padding: '12px', border: '1px solid #bae6fd', borderRadius: '6px', outline: 'none', fontSize: '14px' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 700, color: '#0369a1', display: 'block', marginBottom: '8px' }}>전송 메모</label>
                                <input 
                                    type="text" 
                                    placeholder={sendMode === 'auto' ? "예: {YYYY}년 {MM}월 정기 운행 보고서입니다." : "예: 2026년 3월 정기 운행 보고서입니다."} 
                                    value={memo}
                                    onChange={(e) => setMemo(e.target.value)}
                                    style={{ width: '100%', padding: '12px', border: '1px solid #bae6fd', borderRadius: '6px', outline: 'none', fontSize: '14px' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="tax-log-footer" style={{ padding: '20px 28px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button className="log-btn-cancel" onClick={onClose} style={{ padding: '10px 24px', border: '1px solid #cbd5e1', background: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>취소</button>
                    {(sendMode === 'auto' && hasExistingConfig) && (
                        <button 
                            onClick={handleDeleteConfig}
                            style={{ padding: '10px 24px', border: '1px solid #ef4444', background: 'white', color: '#ef4444', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}
                        >
                            예약 취소(삭제)
                        </button>
                    )}
                    <button 
                        className="log-btn-send" 
                        onClick={handleSend} 
                        disabled={!email || (sendMode === 'instant' && !isPeriodSelected)} 
                        style={{ 
                            padding: '10px 28px', 
                            background: (email && (sendMode === 'auto' || isPeriodSelected)) ? '#2563eb' : '#94a3b8', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px', 
                            cursor: (email && (sendMode === 'auto' || isPeriodSelected)) ? 'pointer' : 'not-allowed', 
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {sendMode === 'auto' ? (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                                {hasExistingConfig ? '예약 정보 수정 저장' : '정기 자동 발송 예약'}
                            </>
                        ) : (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="22" y1="2" x2="11" y2="13"></line>
                                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                </svg>
                                이메일 전송하기
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
